/**
 * QueryBoundary — the per-feature helper that wires the 5-state DoD for a referral screen in one place,
 * plus the CLOUD-only twist: when the query 404s because the module is unmounted on a local install, it
 * renders <UnavailableState/> instead of the generic <ErrorState/>.
 *
 * Order (matches every screen's manual ladder): sans-droit → hors-ligne → indisponible (cloud 404) →
 * chargement → erreur → (children render the loaded/empty body). ForbiddenState for the route as a whole
 * is handled by ProtectedRoute; the `forbidden` prop here covers a per-screen capability the route gate
 * does not (the module gate is empty — see index.tsx).
 */
import type { ReactNode } from "react";
import { ErrorState, ForbiddenState, LoadingState, OfflineState } from "@/ui";
import { useUiStore } from "@/app";
import type { ApiError } from "@/api";
import { isModuleUnmounted } from "../api";
import { UnavailableState } from "./UnavailableState";

export interface QueryBoundaryProps {
  /** True when the user lacks the screen's read capability → ForbiddenState. */
  forbidden: boolean;
  forbiddenCapabilities: ReadonlyArray<string>;
  /** The query state. */
  isLoading: boolean;
  isError: boolean;
  error: ApiError | null;
  /** Whether the cache already holds data (drives the OfflineState `cached` hint). */
  hasData: boolean;
  onRetry: () => void;
  /** Rendered above every state so the screen header stays visible. */
  header?: ReactNode;
  /** The loaded body — rendered only once the query has resolved without error. */
  children: ReactNode;
}

export function QueryBoundary({
  forbidden,
  forbiddenCapabilities,
  isLoading,
  isError,
  error,
  hasData,
  onRetry,
  header,
  children,
}: QueryBoundaryProps) {
  const online = useUiStore((s) => s.online);

  if (forbidden) return <ForbiddenState requiredCapabilities={forbiddenCapabilities} />;

  // The cloud-only swap takes precedence over offline/error: a 404 from an unmounted module is a stable
  // "this install does not have referral", not a transient failure.
  if (isError && isModuleUnmounted(error)) {
    return (
      <Frame>
        {header}
        <UnavailableState />
      </Frame>
    );
  }

  if (!online) {
    return (
      <Frame>
        {header}
        <OfflineState onRetry={onRetry} cached={hasData} />
      </Frame>
    );
  }
  if (isLoading) {
    return (
      <Frame>
        {header}
        <LoadingState variant="table" rows={6} />
      </Frame>
    );
  }
  if (isError) {
    return (
      <Frame>
        {header}
        <ErrorState
          {...(error?.message ? { message: error.message } : {})}
          {...(error?.code ? { code: error.code } : {})}
          onRetry={onRetry}
        />
      </Frame>
    );
  }

  return <Frame>{children}</Frame>;
}

function Frame({ children }: { children: ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>{children}</div>
  );
}
